import Form from '../form'
import OrderThrottlingRow from './order-throttling-row'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import useAllActions from '@hooks/use-all-actions'
import { Button, Grid, Typography } from '@mui/material'
import { Store } from '@types'
import { formatNextDayInZone } from '@utils/date-utils'
import { getErrorMessage } from '@utils/string-utils'
import { makeStyles } from 'tss-react/mui'

/**
 * The fields in this form are specifically saved as pending changes
 * when they are updated. The form is specifically regarding the
 * order throttling settings.
 */
const OrderThrottlingForm = ({
  canEdit,
  className,
  onChange,
  onSubmit,
  paper,
  store,
  title,
}: Props): ReactElement => {
  const { classes } = useStyles()
  const { setFlashMessage } = useAllActions()

  const [values, setValues] = useState<ThrottlingValues>(() =>
    initialValues(store),
  )
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)

  // Reseed when the *throttling* part of the store changes — not on every store
  // update, or an unrelated save elsewhere on the page would wipe out typing.
  const signature = throttlingSignature(store)
  useEffect(() => {
    setValues(initialValues(store))
    setServerErrors({})
  }, [signature])

  const clientErrors = useMemo(() => validate(values), [values])
  const isValid = Object.keys(clientErrors).length === 0

  const payload = buildPayload(values, store, canEdit)
  const changedCount = Object.keys(payload).length
  const pendingCount = countPendingThrottling(store)

  const handleFieldChange = (key: ThrottlingField) => (text: string) => {
    const next = { ...values, [key]: text }
    setValues(next)
    setServerErrors((previous) =>
      Object.fromEntries(
        Object.entries(previous).filter(([field]) => field !== key),
      ),
    )
    onChange?.(next, Object.keys(validate(next)).length === 0)
  }

  const handleCancelPending = (key: ThrottlingField) => () => {
    const next = { ...values, [key]: String(store?.[key] ?? '') }
    setValues(next)
    onChange?.(next, Object.keys(validate(next)).length === 0)
  }

  const handleSubmit = async () => {
    if (!onSubmit || changedCount === 0) return

    setServerErrors({})
    setIsSaving(true)

    try {
      await onSubmit(payload)
      setFlashMessage('Order throttling updated.', 'success')
    } catch (error) {
      const fieldErrors = getFieldErrors(error)
      if (fieldErrors) setServerErrors(fieldErrors)
      setFlashMessage(submitErrorMessage(error), 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const renderFooter = () => {
    if (!store || pendingCount === 0) return null

    const day = formatNextDayInZone(store.time_zone)
    const isSingle = pendingCount === 1

    return (
      <Typography className={classes.footer} variant='body2'>
        <strong>
          {isSingle ? '1 change takes' : `${pendingCount} changes take`} effect{' '}
          {day}
        </strong>{' '}
        — {isSingle ? 'it applies' : 'they apply'} to orders placed that day in
        the store&apos;s time zone ({store.time_zone}).
        {canEdit &&
          ` Re-enter ${
            isSingle
              ? 'the current value to cancel it'
              : 'a current value to cancel one'
          }.`}
      </Typography>
    )
  }

  return (
    <Form className={className} paper={paper} title={title}>
      <Grid container spacing={4}>
        {THROTTLING_FIELDS.map((field) => (
          <OrderThrottlingRow
            canEdit={canEdit !== false}
            error={serverErrors[field.key] || clientErrors[field.key]}
            helperText={field.helperText}
            inForce={store?.[field.key]}
            key={field.key}
            label={field.label}
            max={field.max}
            min={field.min}
            onCancelPending={handleCancelPending(field.key)}
            onChange={handleFieldChange(field.key)}
            pending={pendingValueFor(store, field.key)}
            pendingDay={store && formatNextDayInZone(store.time_zone)}
            value={values[field.key]}
          />
        ))}
        {store && (
          <OrderThrottlingRow
            canEdit={false}
            inForce={store[ORDER_TIME_SLOT_FIELD]}
            label='Pickup Slot Length'
            // Cancelling a pending change means PATCHing the field, which is the
            // write we are deliberately not making. Status only, no affordance.
            onChange={noop}
            pending={pendingValueFor(store, ORDER_TIME_SLOT_FIELD)}
            pendingDay={formatNextDayInZone(store.time_zone)}
            readOnly
            readOnlyNote='Not editable from admin.'
            value={`${store[ORDER_TIME_SLOT_FIELD]} minutes`}
          />
        )}
      </Grid>
      {renderFooter()}
      {canEdit && onSubmit && (
        <Button
          className={classes.submitButton}
          color='secondary'
          disabled={!isValid || changedCount === 0 || isSaving}
          onClick={handleSubmit}
          variant='contained'
        >
          Submit
        </Button>
      )}
    </Form>
  )
}

export default OrderThrottlingForm

/**
 * The editable throttling fields. `order_time_slot_minutes` is deliberately not
 * here: it is display-only, so it gets no draft state and can never reach a
 * payload. Bounds mirror the API's, which enforces them regardless.
 */
const THROTTLING_FIELDS = [
  {
    key: 'cups_per_barista',
    label: 'Cups per Barista',
    min: 1,
    max: 9,
    defaultValue: 2,
    helperText: 'Cups one barista can make in a single time slot.',
  },
  {
    key: 'max_cups_in_cart',
    label: 'Max Cups per Order',
    min: 1,
    max: 9,
    defaultValue: 4,
    helperText:
      'Largest beverage order accepted. May exceed cups per barista — such an order occupies one barista for the whole slot.',
  },
  {
    key: 'max_prep_time',
    label: 'Max Prep Time',
    min: 1,
    max: 5000,
    defaultValue: 150,
    helperText: 'Total appliance cook time available per slot.',
  },
  {
    key: 'max_food_items_in_cart',
    label: 'Max Food Items per Order',
    min: 1,
    max: 999,
    defaultValue: 4,
    helperText: 'Most food items allowed in one order.',
  },
  {
    key: 'max_bags_in_cart',
    label: 'Max Coffee Bags per Order',
    min: 1,
    max: 999,
    defaultValue: 8,
    helperText: 'Most coffee bags allowed in one order.',
  },
  {
    key: 'max_merchandise_in_cart',
    label: 'Max Merchandise per Order',
    min: 1,
    max: 999,
    defaultValue: 8,
    helperText: 'Most merchandise items allowed in one order.',
  },
  {
    key: 'max_togo_in_cart',
    label: 'Max To-Go Items per Order',
    min: 1,
    max: 999,
    defaultValue: 99,
    helperText: 'Most to-go items allowed in one order.',
  },
] as const

const ORDER_TIME_SLOT_FIELD = 'order_time_slot_minutes'

export type ThrottlingField = (typeof THROTTLING_FIELDS)[number]['key']
export type ThrottlingValues = Record<ThrottlingField, string>
export type ThrottlingPayload = Partial<Record<ThrottlingField, number>>

type Props = {
  canEdit?: boolean
  className?: string
  /** Embedded use (store creation): reports drafts up, renders no submit. */
  onChange?: (values: ThrottlingValues, isValid: boolean) => void
  /** Standalone use: receives only the changed fields. Renders the submit. */
  onSubmit?: (payload: ThrottlingPayload) => Promise<unknown> | unknown
  paper?: boolean
  /** Omit for a store that doesn't exist yet — nothing can be pending. */
  store?: Store
  title?: string
}

const noop = () => undefined

/** Values a brand new store starts with, so a creator need not touch them. */
export const throttlingDefaults = (): ThrottlingValues =>
  THROTTLING_FIELDS.reduce(
    (defaults, field) => ({
      ...defaults,
      [field.key]: String(field.defaultValue),
    }),
    {} as ThrottlingValues,
  )

export const parseThrottlingValues = (
  values: ThrottlingValues,
): ThrottlingPayload =>
  THROTTLING_FIELDS.reduce(
    (parsed, field) => ({
      ...parsed,
      [field.key]: parseInt(values[field.key], 10),
    }),
    {} as ThrottlingPayload,
  )

/**
 * `pending_updates` is a shared queue — other features add their own keys, with
 * their own value types. Only read the ones this form owns, and only as numbers.
 */
const pendingValueFor = (
  store: Store | undefined,
  field: string,
): number | undefined => {
  const value = store?.pending_updates?.[field]

  return typeof value === 'number' ? value : undefined
}

/** How many throttling changes are waiting. */
export const countPendingThrottling = (store?: Store): number => {
  if (!store) return 0

  return [...THROTTLING_FIELDS.map((field) => field.key), ORDER_TIME_SLOT_FIELD]
    .map((field) => pendingValueFor(store, field))
    .filter((value) => value != null).length
}

/** What the field will be: the pending value if one is waiting, else the live one. */
const desiredFor = (store: Store, field: ThrottlingField): number =>
  pendingValueFor(store, field) ?? store[field]

const initialValues = (store?: Store): ThrottlingValues => {
  if (!store) return throttlingDefaults()

  return THROTTLING_FIELDS.reduce(
    (values, field) => ({
      ...values,
      [field.key]: String(desiredFor(store, field.key)),
    }),
    {} as ThrottlingValues,
  )
}

/** Changes to these values, and nothing else, should reseed the draft. */
const throttlingSignature = (store?: Store): string =>
  JSON.stringify(
    THROTTLING_FIELDS.map((field) => [
      store?.[field.key],
      pendingValueFor(store, field.key),
    ]),
  )

const validate = (values: ThrottlingValues): Record<string, string> =>
  THROTTLING_FIELDS.reduce((errors, field) => {
    const raw = values[field.key]
    const parsed = Number(raw)
    const isWhole = raw.trim() !== '' && Number.isInteger(parsed)

    if (!isWhole || parsed < field.min || parsed > field.max) {
      return {
        ...errors,
        [field.key]: `Must be a whole number between ${field.min} and ${field.max}.`,
      }
    }

    return errors
  }, {})

/**
 * Only the fields the user actually changed. Diffed against the pending value
 * rather than the live one, so re-entering the live value reads as a change and
 * gets sent — which is what withdraws a pending update.
 */
const buildPayload = (
  values: ThrottlingValues,
  store?: Store,
  canEdit?: boolean,
): ThrottlingPayload => {
  // A throttling key sent without the permission 403s the whole request, so
  // never build one. The disabled inputs are the first line of defence; this is
  // the second.
  if (!store || canEdit === false) return {}

  return THROTTLING_FIELDS.reduce((payload, field) => {
    const parsed = parseInt(values[field.key], 10)

    if (!Number.isInteger(parsed) || parsed === desiredFor(store, field.key)) {
      return payload
    }

    return { ...payload, [field.key]: parsed }
  }, {})
}

/** A 400 carries `{field: [message]}`; anything else has no per-field detail. */
const getFieldErrors = (error: any): Record<string, string> | null => {
  const errors = error?.errors

  if (!errors || typeof errors !== 'object') return null

  return Object.entries(errors).reduce((mapped, [field, messages]) => {
    const message = Array.isArray(messages) ? messages[0] : messages

    return typeof message === 'string'
      ? { ...mapped, [field]: message }
      : mapped
  }, {})
}

const submitErrorMessage = (error: any): string =>
  error?.status === 403
    ? 'You do not have permission to change order throttling. Nothing was saved.'
    : getErrorMessage(error)

const useStyles = makeStyles()((theme) => ({
  submitButton: {
    marginTop: theme.spacing(3),
    color: 'white',
  },
  footer: {
    marginTop: theme.spacing(3),
    color: theme.palette.warning.dark,
  },
}))
